import React, { useEffect, useRef } from "react";
import "./roompage.css";
import VideoSection from "./VideoSection/VideoSection";
import Overlay from "./Overlay";
import MeetingTimer from "../joinroompage/MeetingTimer";
import { connect, useSelector } from "react-redux";
import * as webRTCHandler from "./../utils/webRTCHandler";
import { setFromJid, setToJid, setCloseWindow } from "../store/actions";
import { useSearchParams } from "react-router-dom";

// Toast for popup alert
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Redux store import for state access
import store from "../store/store";
import { stopRingtone } from "../utils/ringtone";

const Roompage = ({
  roomId,
  identity,
  isRoomHost,
  showOverlay,
  connectOnlyWithAudio,
  fromJid,
  toJid,
  setCloseWindowAction,
  callConnected,
}) => {
  const [searchParams] = useSearchParams();
  const callTimeoutRef = useRef(null);

  const previewVideoEnabled = useSelector((state) => state.previewVideoEnabled);
  const previewAudioEnabled = useSelector((state) => state.previewAudioEnabled);

  useEffect(() => {
    if (callConnected) {
      console.log("Call connected - clearing timeout from Room component");
      clearCallTimeout();
      localStorage.removeItem("expectCallTimeout");
      localStorage.removeItem("missedCallTimeout");
      localStorage.removeItem("missedCallName");
    }
  }, [callConnected]);
  
  const clearCallTimeout = () => {
    if (callTimeoutRef.current) {
      clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = null;
    }
  };

  // ✨ Handle meeting end from timer
  const handleMeetingEnd = () => {
    console.log("⏱️ Meeting time has ended - initiating cleanup");
    
    try {
      // Stop ringtone if playing
      stopRingtone();
      
      // Stop all local media streams
      const localStream = webRTCHandler.getLocalStream();
      if (localStream) {
        console.log("🎥 Stopping local media streams");
        localStream.getTracks().forEach(track => {
          track.stop();
          console.log(`  Stopped track: ${track.kind}`);
        });
      }

      // Leave the room
      console.log("🚪 Leaving room");
      webRTCHandler.leaveRoom();
      
      // Clear any timeouts
      clearCallTimeout();
      
      // Clean up localStorage
      localStorage.removeItem("expectCallTimeout");
      localStorage.removeItem("missedCallTimeout");
      localStorage.removeItem("missedCallName");
      localStorage.removeItem("backrefresh");
      // localStorage.removeItem("previewVideoEnabled");
      // localStorage.removeItem("previewAudioEnabled");
      
    } catch (error) {
      console.error("❌ Error during meeting cleanup:", error);
    }
    
    // Show notification
    toast.info("Meeting time has ended. Redirecting...", {
      position: "top-center",
      autoClose: 2000,
    });
    
    // Redirect to home page after 3 seconds
    setTimeout(() => {
      const siteUrl = window.location.origin;
      const authData = store.getState().cofferAuthData;
      const cofferToken = store.getState().cofferToken;
      const myJid = store.getState().fromJid;
      const identity = store.getState().identity;
      const loginType = store.getState().loginType;
      const hideClient = store.getState().hideClient;

      if (cofferToken && myJid) {
        window.location.href = `${siteUrl}?logintype=${encodeURIComponent(
          loginType
        )}&token=${cofferToken}&jid=${encodeURIComponent(
          myJid
        )}&name=${encodeURIComponent(
          identity
        )}&hideclient=${hideClient}&back=0`;
      } else {
        window.location.href = siteUrl;
      }
    }, 3000);
  };

  useEffect(() => {
    const startCallTimeout = () => {
      const timeoutValue = parseInt(localStorage.getItem("missedCallTimeout")) || 10000;
      callTimeoutRef.current = setTimeout(() => {
        if (localStorage.getItem("expectCallTimeout") === "true") {
          const missedName = localStorage.getItem("missedCallName") || "User";
          localStorage.setItem("missedCall", missedName);
          localStorage.removeItem("expectCallTimeout");
          stopRingtone();
          const siteUrl = window.location.origin;
          const authData = store.getState().cofferAuthData;
          const cofferToken = store.getState().cofferToken;
          const myJid = store.getState().fromJid;
          const identity = store.getState().identity;
          const loginType = store.getState().loginType;
          const hideClient = store.getState().hideClient;

          localStorage.setItem("backrefresh", "0");

          window.location.href = `${siteUrl}?logintype=${encodeURIComponent(
            loginType
          )}&token=${cofferToken}&jid=${encodeURIComponent(
            myJid
          )}&name=${encodeURIComponent(
            identity
          )}&hideclient=${hideClient}&back=0`;
        }
      }, timeoutValue);
    };

    clearCallTimeout();

    if (
      searchParams.get("roomid") === null ||
      searchParams.get("identity") === null
    ) {
      setCloseWindowAction(false);

      if (!isRoomHost && !roomId) {
        const siteUrl = window.location.origin;
        window.location.href = siteUrl;
      } else {
        localStorage.setItem("backrefresh", "1");
        
        console.log("📹 Camera setting from preview:", previewVideoEnabled);
        console.log("🎤 Microphone setting from preview:", previewAudioEnabled);
        
        // Determine if we're in audio-only mode based on video setting
        // If previewVideoEnabled is 'false', then we're in audio-only mode
        let finalConnectOnlyWithAudio = !previewVideoEnabled;
        
        
        console.log("Final settings for room:");
        console.log("  - connectOnlyWithAudio:", finalConnectOnlyWithAudio);

        webRTCHandler.getLocalPreviewAndInitRoomConnection(
          isRoomHost,
          identity,
          roomId,
          finalConnectOnlyWithAudio,
          fromJid,
          toJid
        );

        if (isRoomHost) startCallTimeout();
      }
    } else {
      setCloseWindowAction(true);
      const room = searchParams.get("roomid");
      const myName = searchParams.get("identity");

      localStorage.setItem("backrefresh", "1");
      
      let finalConnectOnlyWithAudio = !previewVideoEnabled;
      

      webRTCHandler.getLocalPreviewAndInitRoomConnection(
        false,
        myName,
        room,
        finalConnectOnlyWithAudio,
        fromJid,
        toJid
      );
    }

    return () => {
      clearCallTimeout();
    };
  }, []);

  return (
    <div className="room_container">
      <MeetingTimer onMeetingEnd={handleMeetingEnd} />
      
      <VideoSection />
      {showOverlay && <Overlay />}
      <ToastContainer position="top-center" />
    </div>
  );
};

const mapStoreStateToProps = (state) => ({ 
  ...state,
  callConnected: state.callConnected || false, 
});

const mapActionsToProps = (dispatch) => ({
  setFromJidAction: (fromJid) => dispatch(setFromJid(fromJid)),
  setToJidAction: (toJid) => dispatch(setToJid(toJid)),
  setCloseWindowAction: (closeWindow) =>
    dispatch(setCloseWindow(closeWindow)),
});

export default connect(mapStoreStateToProps, mapActionsToProps)(Roompage);